<template>
  <v-card
    class="embedding-desktop-card"
    color="background"
    variant="elevated"
    elevation="4"
    @click="emitEdit"
  >
    <div class="embedding-shell">
      <div class="embedding-header">
        <div class="embedding-header-main">
          <div class="embedding-provider-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${config.providerKey || 'embedding'} icon`"
              class="embedding-provider-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="embedding-provider-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${config.providerKey || 'embedding'} icon`"
            ></span>
            <span v-else class="embedding-provider-avatar-fallback" :style="{ backgroundColor: brandColor }">{{ providerInitials }}</span>
          </div>
          <div class="embedding-title-wrap">
            <div class="embedding-title-row">
              <div class="embedding-title">{{ config.name }}</div>
            </div>
            <div class="embedding-subtitle">{{ config.providerKey || 'No provider' }}</div>
          </div>
        </div>

        <div class="embedding-header-actions">
          <v-tooltip text="Edit Embedding Model" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-pencil-outline"
                size="small"
                variant="plain"
                class="embedding-edit-icon"
                @click.stop="emitEdit"
              />
            </template>
          </v-tooltip>
        </div>
      </div>

      <div class="embedding-spec-grid">
        <div class="embedding-spec">
          <div class="embedding-spec-k">Model</div>
          <div class="embedding-spec-v embedding-spec-v-mono">{{ modelChipLabel }}</div>
        </div>
        <div class="embedding-spec">
          <div class="embedding-spec-k">Usage</div>
          <div class="embedding-spec-v">{{ collectionUsageLabel }}</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { EmbeddingModelConfig, SystemEmbeddingModelConfig, UserEmbeddingModelConfig, VectorStoreWithFiles } from '@/types/embeddings'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'

@Options({
  props: {
    model: Object
  },
  computed: {
    ...mapGetters(['vectorStores', 'currentTheme'])
  },
  emits: ['edit']
})
export default class EmbeddingModelSummaryCard extends Vue {
  model!: SystemEmbeddingModelConfig | UserEmbeddingModelConfig | null
  vectorStores!: Record<string, VectorStoreWithFiles>
  currentTheme!: string
  iconLoadFailed = false

  get config(): EmbeddingModelConfig {
    return (this.model?.config as EmbeddingModelConfig) || {
      name: 'Unknown embedding model',
      modelName: '',
      providerKey: ''
    }
  }

  get modelChipLabel(): string {
    return this.config.modelName || this.config.name || ''
  }

  get providerInitials(): string {
    const raw = String(this.config.providerKey || '').replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'EM'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.config.providerKey)
  }

  get icon(): ProviderIcon | null {
    return getProviderIcon(this.config.providerKey, this.currentTheme as ThemeName)
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  get collectionUsageCount(): number {
    if (!this.vectorStores || !this.config.name) return 0
    const target = this.config.name.toLowerCase()
    let count = 0
    for (const store of Object.values(this.vectorStores)) {
      if (store.embeddingModel && store.embeddingModel.toLowerCase() === target) {
        count++
      }
    }
    return count
  }

  get collectionUsageLabel(): string {
    const count = this.collectionUsageCount
    if (count === 0) return 'Unused'
    return count === 1 ? 'Used by 1 collection' : `Used by ${count} collections`
  }

  emitEdit() {
    this.$emit('edit', this.config.id)
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }
}
</script>

<style scoped lang="scss">
.embedding-desktop-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.embedding-shell {
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
}

.embedding-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.embedding-header-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.embedding-provider-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}

.embedding-provider-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.embedding-provider-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.embedding-provider-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}

.embedding-title-wrap {
  min-width: 0;
}

.embedding-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.embedding-title {
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.embedding-subtitle {
  margin-top: 4px;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.embedding-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.embedding-edit-icon {
  opacity: 0;
  transition: opacity 0.18s ease;
}

.embedding-desktop-card:hover .embedding-edit-icon,
.embedding-desktop-card:focus-within .embedding-edit-icon {
  opacity: 1;
}

.embedding-spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  padding: 14px 0 0;
  margin-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.embedding-spec-k {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 4px;
}

.embedding-spec-v {
  font-size: 0.95rem;
  line-height: 1.35;
  font-weight: 600;
  word-break: break-word;
}

.embedding-spec-v-mono {
  font-family: var(--font-mono, monospace);
  font-size: 0.86rem;
}
</style>
